import React, { useState } from 'react';
import { toast } from 'sonner';
import { 
  ArrowLeft, User, Calendar, Star, Hand, Heart, 
  Sparkles, Diamond, Download, Globe, Loader2
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa6';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Report Types Configuration
const REPORT_TYPES = [
  { type: 'numerology_name', name: 'Name Numerology', icon: User, description: 'Analyze your name vibrations', color: 'purple' },
  { type: 'numerology_dob', name: 'DOB Numerology', icon: Calendar, description: 'Birth date life path', color: 'amber' },
  { type: 'kundali', name: 'Kundali Report', icon: Star, description: 'Complete birth chart', color: 'orange' },
  { type: 'palmistry', name: 'Palm Reading', icon: Hand, description: 'AI palm analysis', color: 'pink' },
  { type: 'face_reading', name: 'Face Reading', icon: User, description: 'AI face analysis (Mukhakriti)', color: 'indigo' },
  { type: 'match_making', name: 'Match Making', icon: Heart, description: 'Compatibility report', color: 'red' },
  { type: 'daily_horoscope', name: 'Daily Horoscope', icon: Sparkles, description: "Today's prediction", color: 'cyan' },
];

// Supported Languages
const REPORT_LANGUAGES = [
  { code: 'hi', name: 'Hindi', native: 'Hindi' },
  { code: 'mr', name: 'Marathi', native: 'Marathi' },
  { code: 'gu', name: 'Gujarati', native: 'Gujarati' },
  { code: 'bn', name: 'Bengali', native: 'Bengali' },
  { code: 'ta', name: 'Tamil', native: 'Tamil' },
  { code: 'te', name: 'Telugu', native: 'Telugu' },
  { code: 'kn', name: 'Kannada', native: 'Kannada' },
  { code: 'ml', name: 'Malayalam', native: 'Malayalam' },
  { code: 'pa', name: 'Punjabi', native: 'Punjabi' },
  { code: 'en', name: 'English', native: 'English' },
];

// Zodiac Signs for Daily Horoscope
const ZODIAC_SIGNS = [
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
];

const COLOR_CLASSES = {
  purple: 'from-purple-500 to-purple-700 border-purple-500/30',
  blue: 'from-blue-500 to-blue-700 border-blue-500/30',
  amber: 'from-amber-500 to-amber-700 border-amber-500/30',
  orange: 'from-orange-500 to-orange-700 border-orange-500/30',
  pink: 'from-pink-500 to-pink-700 border-pink-500/30',
  red: 'from-red-500 to-red-700 border-red-500/30',
  cyan: 'from-cyan-500 to-cyan-700 border-cyan-500/30',
  indigo: 'from-indigo-500 to-indigo-700 border-indigo-500/30',
};

// Glow Button Component
const GlowButton = ({ children, onClick, className = "", variant = "primary", disabled = false }) => {
  const variants = {
    primary: "bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500",
    gold: "bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-400 hover:to-yellow-500",
    diamond: "bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500",
    success: "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500",
  };
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        ${variants[variant]} 
        text-white font-bold py-3 px-6 rounded-xl
        shadow-lg transition-all duration-300
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 active:scale-95'}
        ${className}
      `}
    >
      {children}
    </button>
  );
};

// Report Form Component
const ReportForm = ({ 
  selectedReportType, 
  reportForm, 
  setReportForm, 
  reportLanguage, 
  setReportLanguage,
  onGenerate,
  generatingReport,
  onBack 
}) => {
  const reportType = REPORT_TYPES.find(r => r.type === selectedReportType);
  
  const handleFileUpload = (field, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const base64 = ev.target.result.split(',')[1];
        setReportForm({...reportForm, [field]: base64});
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="max-w-lg mx-auto">
      <div className="bg-slate-800/50 rounded-xl p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={onBack} className="text-gray-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h3 className="text-xl font-bold text-white">{reportType?.name}</h3>
        </div>
        
        {/* Language Selector */}
        <div className="mb-6 p-4 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-xl border border-amber-500/30">
          <label className="text-amber-400 font-bold mb-3 block flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Select Report Language
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {REPORT_LANGUAGES.map(lang => (
              <button
                key={lang.code}
                onClick={() => setReportLanguage(lang.code)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  reportLanguage === lang.code 
                    ? 'bg-amber-500 text-black' 
                    : 'bg-slate-700 text-white hover:bg-slate-600'
                }`}
              >
                {lang.native}
              </button>
            ))}
          </div>
          <p className="text-amber-300/70 text-xs mt-2">
            Your report will be generated in {REPORT_LANGUAGES.find(l => l.code === reportLanguage)?.native}
          </p>
        </div>
        
        {/* Full Name Field */}
        {['numerology_name', 'numerology_dob', 'kundali', 'palmistry', 'face_reading', 'match_making'].includes(selectedReportType) && (
          <div className="mb-4">
            <label className="text-white font-medium mb-2 block">Full Name</label>
            <input
              type="text"
              value={reportForm.full_name || ''}
              onChange={(e) => setReportForm({...reportForm, full_name: e.target.value})}
              placeholder="Enter full name"
              className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
              data-testid="report-full-name"
            />
          </div>
        )}
        
        {/* DOB Fields */}
        {['numerology_dob', 'kundali', 'match_making'].includes(selectedReportType) && (
          <div className="mb-4">
            <label className="text-white font-medium mb-2 block">Date of Birth</label>
            <input
              type="date"
              value={reportForm.date_of_birth || ''}
              onChange={(e) => setReportForm({...reportForm, date_of_birth: e.target.value})}
              className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
              data-testid="report-dob"
            />
          </div>
        )}
        
        {/* Kundali / Match Making: birth time and place */}
        {['kundali', 'match_making'].includes(selectedReportType) && (
          <>
            <div className="mb-4">
              <label className="text-white font-medium mb-2 block">{selectedReportType === 'match_making' ? 'Your Birth Time' : 'Birth Time'}</label>
              <input
                type="time"
                value={reportForm.birth_time || ''}
                onChange={(e) => setReportForm({...reportForm, birth_time: e.target.value})}
                className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
                data-testid="report-birth-time"
              />
            </div>
            <div className="mb-4">
              <label className="text-white font-medium mb-2 block">{selectedReportType === 'match_making' ? 'Your Birth Place' : 'Birth Place'}</label>
              <input
                type="text"
                value={reportForm.birth_place || ''}
                onChange={(e) => setReportForm({...reportForm, birth_place: e.target.value})}
                placeholder="City, State, Country"
                className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
                data-testid="report-birth-place"
              />
            </div>
          </>
        )}
        
        {/* Palmistry Image Upload */}
        {selectedReportType === 'palmistry' && (
          <div className="mb-4">
            <label className="text-white font-medium mb-2 block">Palm Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileUpload('palm_image_base64', e)}
              className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700"
              data-testid="palmistry-image-upload"
            />
            <p className="text-gray-500 text-xs mt-1">Upload a clear photo of your palm</p>
          </div>
        )}
        
        {/* Face Reading Image Upload */}
        {selectedReportType === 'face_reading' && (
          <div className="mb-4">
            <label className="text-white font-medium mb-2 block">Face Photo (Clear Selfie)</label>
            <input
              type="file"
              accept="image/*"
              capture="user"
              onChange={(e) => handleFileUpload('face_image_base64', e)}
              className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700"
              data-testid="face-reading-image-upload"
            />
            <p className="text-gray-500 text-xs mt-1">Take or upload a clear front-facing photo</p>
          </div>
        )}
        
        {/* Match Making Partner Fields */}
        {selectedReportType === 'match_making' && (
          <>
            <div className="border-t border-slate-700 my-4 pt-4">
              <h4 className="text-amber-400 font-bold mb-3">Partner Details</h4>
            </div>
            <div className="mb-4">
              <label className="text-white font-medium mb-2 block">Partner Name</label>
              <input
                type="text"
                value={reportForm.partner_name || ''}
                onChange={(e) => setReportForm({...reportForm, partner_name: e.target.value})}
                placeholder="Enter partner's full name"
                className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
                data-testid="partner-name"
              />
            </div>
            <div className="mb-4">
              <label className="text-white font-medium mb-2 block">Partner DOB</label>
              <input
                type="date"
                value={reportForm.partner_dob || ''}
                onChange={(e) => setReportForm({...reportForm, partner_dob: e.target.value})}
                className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
                data-testid="partner-dob"
              />
            </div>
            <div className="mb-4">
              <label className="text-white font-medium mb-2 block">Partner Birth Time</label>
              <input
                type="time"
                value={reportForm.partner_birth_time || ''}
                onChange={(e) => setReportForm({...reportForm, partner_birth_time: e.target.value})}
                className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
                data-testid="partner-birth-time"
              />
            </div>
            <div className="mb-4">
              <label className="text-white font-medium mb-2 block">Partner Birth Place</label>
              <input
                type="text"
                value={reportForm.partner_birth_place || ''}
                onChange={(e) => setReportForm({...reportForm, partner_birth_place: e.target.value})}
                placeholder="City, State, Country"
                className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
                data-testid="partner-birth-place"
              />
            </div>
          </>
        )}
        
        {/* Daily Horoscope Zodiac Selection */}
        {selectedReportType === 'daily_horoscope' && (
          <div className="mb-4">
            <label className="text-white font-medium mb-2 block">Zodiac Sign</label>
            <select
              value={reportForm.zodiac_sign || ''}
              onChange={(e) => setReportForm({...reportForm, zodiac_sign: e.target.value})}
              className="w-full px-4 py-3 bg-slate-900 text-white rounded-xl border border-slate-700 focus:border-amber-500"
              data-testid="zodiac-select"
            >
              <option value="">Select your zodiac sign</option>
              {ZODIAC_SIGNS.map(sign => (
                <option key={sign} value={sign.toLowerCase()}>{sign}</option>
              ))}
            </select>
          </div>
        )}
        
        {/* Generate Button */}
        <GlowButton 
          onClick={onGenerate} 
          variant="diamond" 
          className="w-full mt-4"
          disabled={generatingReport}
          data-testid="generate-report-btn"
        >
          {generatingReport ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              Generating Report...
            </span>
          ) : (
            'Generate Report'
          )}
        </GlowButton>
      </div>
    </div>
  );
};

// Report Card Component
const ReportCard = ({ report, token }) => {
  const [downloading, setDownloading] = useState(false);
  
  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      // Use the correct endpoint: /api/sankat-mochan/report/{report_id}/download
      const res = await fetch(`${API_URL}/api/sankat-mochan/report/${report.id || report._id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `report_${report.report_type}_${report.language || 'en'}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success('PDF downloaded successfully!');
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to download PDF');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Error downloading PDF');
    }
    setDownloading(false);
  };
  
  const handleWhatsAppShare = () => {
    const reportPreview = report.content?.substring(0, 200) || 'Check out my AI report!';
    const message = encodeURIComponent(
      `${reportPreview}...\n\nGet your own AI report at Sankat Mochan Samadhan!`
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
    toast.success('Opening WhatsApp...');
  };
  
  return (
    <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="text-white font-bold capitalize">
            {report.report_type?.replace(/_/g, ' ')}
          </h4>
          <p className="text-gray-400 text-xs">
            {new Date(report.created_at).toLocaleDateString()}
            {report.language && (
              <span className="ml-2 px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs">
                {REPORT_LANGUAGES.find(l => l.code === report.language)?.native || report.language}
              </span>
            )}
          </p>
        </div>
      </div>
      
      <p className="text-gray-300 text-sm line-clamp-3 mb-4">
        {report.content?.substring(0, 150)}...
      </p>
      
      <div className="flex gap-2">
        <button
          onClick={handleDownloadPDF}
          disabled={downloading}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-all"
          data-testid="download-pdf-btn"
        >
          {downloading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
          Download PDF
        </button>
        <button
          onClick={handleWhatsAppShare}
          className="flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-500 transition-all"
          data-testid="whatsapp-share-btn"
        >
          <FaWhatsapp className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Main Reports View Component
const ReportsView = ({ 
  onBack, 
  userPasses = [],
  userReports = [],
  token,
  onNavigateToPasses,
  fetchUserReports
}) => {
  const [selectedReportType, setSelectedReportType] = useState(null);
  const [reportForm, setReportForm] = useState({});
  const [reportLanguage, setReportLanguage] = useState('hi');
  const [generatingReport, setGeneratingReport] = useState(false);
  
  const diamondPasses = userPasses.filter(
    p => p.status === 'active' && p.pass_type === 'diamond' && (p.reports_limit - p.reports_used) > 0
  );
  
  const handleGenerateReport = async () => {
    if (!diamondPasses.length) {
      toast.error('Diamond pass required');
      return;
    }
    
    // Validation
    if (selectedReportType !== 'daily_horoscope' && !reportForm.full_name) {
      toast.error('Please enter full name');
      return;
    }
    
    if (selectedReportType === 'daily_horoscope' && !reportForm.zodiac_sign) {
      toast.error('Please select zodiac sign');
      return;
    }
    
    if (selectedReportType === 'face_reading' && !reportForm.face_image_base64) {
      toast.error('Please upload a face photo');
      return;
    }
    
    setGeneratingReport(true);
    try {
      const payload = {
        report_type: selectedReportType,
        language: reportLanguage,
        ...reportForm
      };
      
      const res = await fetch(`${API_URL}/api/sankat-mochan/generate-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success('Report generated successfully!');
        setSelectedReportType(null);
        setReportForm({});
        if (fetchUserReports) fetchUserReports();
      } else {
        toast.error(data.detail || 'Failed to generate report');
      }
    } catch (error) {
      toast.error('Error generating report');
    }
    setGeneratingReport(false);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900/20 to-slate-900 p-4 md:p-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="text-white" data-testid="reports-back-btn">
          <ArrowLeft className="w-6 h-6" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">AI Reports</h1>
          <p className="text-gray-400 text-sm">Generate Numerology, Kundali, Palmistry & Face Reading Reports</p>
        </div>
      </div>
      
      {/* Diamond Pass Status */}
      {diamondPasses.length > 0 ? (
        <div className="bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-xl p-4 mb-6 border border-cyan-500/30 max-w-3xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Diamond className="w-8 h-8 text-cyan-400" />
              <div>
                <p className="font-bold text-white">Diamond Pass Active</p>
                <p className="text-gray-400 text-sm">
                  {diamondPasses[0].reports_limit - diamondPasses[0].reports_used} of {diamondPasses[0].reports_limit} reports remaining
                </p>
              </div>
            </div>
            <div className="text-4xl font-black text-cyan-400">
              {diamondPasses[0].reports_limit - diamondPasses[0].reports_used}
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-slate-800/50 rounded-xl p-6 mb-6 text-center max-w-3xl mx-auto">
          <Diamond className="w-12 h-12 text-gray-500 mx-auto mb-3" />
          <p className="text-white font-bold mb-2">Diamond Pass Required</p>
          <p className="text-gray-400 text-sm mb-4">Purchase a Diamond Pass to generate AI reports</p>
          <GlowButton onClick={onNavigateToPasses} variant="diamond" data-testid="buy-diamond-pass-btn">
            Buy Diamond Pass - Rs.990
          </GlowButton>
        </div>
      )}
      
      {/* Report Type Selection or Form */}
      {!selectedReportType ? (
        <>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto mb-8">
            {REPORT_TYPES.map(report => (
              <div
                key={report.type}
                onClick={() => diamondPasses.length > 0 && setSelectedReportType(report.type)}
                className={`
                  bg-gradient-to-br ${COLOR_CLASSES[report.color]} 
                  rounded-xl p-5 cursor-pointer transition-all border
                  ${diamondPasses.length > 0 ? 'hover:scale-105' : 'opacity-50 cursor-not-allowed'}
                `}
                data-testid={`report-${report.type}`}
              >
                <report.icon className="w-10 h-10 text-white mb-3" />
                <h3 className="text-lg font-bold text-white mb-1">{report.name}</h3>
                <p className="text-white/80 text-sm">{report.description}</p>
              </div>
            ))}
          </div>
          
          {/* My Reports Section */}
          {userReports.length > 0 && (
            <div className="max-w-5xl mx-auto">
              <h2 className="text-xl font-bold text-white mb-4">My Reports</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {userReports.map((report, idx) => (
                  <ReportCard key={report._id || idx} report={report} token={token} />
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <ReportForm
          selectedReportType={selectedReportType}
          reportForm={reportForm}
          setReportForm={setReportForm}
          reportLanguage={reportLanguage}
          setReportLanguage={setReportLanguage}
          onGenerate={handleGenerateReport}
          generatingReport={generatingReport}
          onBack={() => setSelectedReportType(null)}
        />
      )}
    </div>
  );
};

export default ReportsView;
